import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faSpinner } from "@fortawesome/free-solid-svg-icons";

type Status = "idle" | "uploading" | "done";

interface UploadIndicatorContextInterface {
  /**
   * Track a background upload. Shows a fixed bottom-corner badge (spinner)
   * while pending; when the promise settles and no other upload is in flight
   * it flips to a green check, then auto-hides after 3s.
   */
  track: (promise: Promise<unknown>) => void;
}

const UploadIndicatorContext = createContext<UploadIndicatorContextInterface>({
  track: () => {},
});

export const useUploadIndicator = () => useContext(UploadIndicatorContext);

const HIDE_DELAY_MS = 3000;

export const UploadIndicatorProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [status, setStatus] = useState<Status>("idle");
  const activeRef = useRef(0);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const track = useCallback((promise: Promise<unknown>) => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    activeRef.current += 1;
    setStatus("uploading");
    Promise.resolve(promise).finally(() => {
      activeRef.current = Math.max(0, activeRef.current - 1);
      if (activeRef.current === 0) {
        setStatus("done");
        hideTimer.current = setTimeout(() => {
          setStatus("idle");
          hideTimer.current = null;
        }, HIDE_DELAY_MS);
      }
    });
  }, []);

  const uploading = status === "uploading";
  const done = status === "done";

  return (
    <UploadIndicatorContext.Provider value={{ track }}>
      {children}
      <div
        aria-live="polite"
        style={{
          position: "fixed",
          right: 20,
          bottom: 20,
          zIndex: 3000,
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 16px",
          borderRadius: 999,
          background: "var(--app-card-bg, #ffffff)",
          border: "1px solid var(--app-gray-200, #e5e7eb)",
          boxShadow: "0 6px 20px rgba(15, 23, 42, 0.18)",
          color: "var(--app-gray-700, #374151)",
          fontSize: 14,
          fontWeight: 600,
          pointerEvents: "none",
          opacity: status === "idle" ? 0 : 1,
          transform: status === "idle" ? "translateY(8px)" : "translateY(0)",
          transition: "opacity 240ms ease, transform 240ms ease",
        }}
        data-testid="upload-indicator"
        data-status={status}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: done ? "#52c41a" : "transparent",
            color: done ? "#ffffff" : "#9c1419",
            transition: "background 240ms ease, color 240ms ease",
          }}
        >
          <FontAwesomeIcon
            icon={done ? faCheck : faSpinner}
            spin={uploading}
          />
        </span>
        <span>{done ? "Salvato" : "Caricamento media…"}</span>
      </div>
    </UploadIndicatorContext.Provider>
  );
};
